<template>
	<div class="container-md mh-100">
		<LayoutHeaderSection>
			<template #title>
				<LayoutTextSection text="Importar clientes" :breadcrumb="true" />
			</template>
		</LayoutHeaderSection>
		<main class="pt-8 vstack gap-10">
			<CommonFormWrapper submitText="Importar" :cancelUrl="{ name: 'company.admin.customer.index' }" :submitting="submitting" :submitStick="true" @submit="submit">
				<template #body>
					<CommonFormInputFile v-model="file" accept=".xls,.xlsx" title="Base de datos de clientes" info="Archivos en formato Excel (.xls y .xlsx)." />
					<CommonFormInputSelect v-model="form.group_id" title="Asignar al grupo" placeholder="Sin especificar" :options="groupsOptions" />
					<LayoutTableBasic :results="form.results" :nowrap="true" :responsive="true" tableClass="table-hover table-sm">
						<template #head>
							<tr>
								<th scope="col" width="30"></th>
								<th class="fw-bold px-1" scope="col"><span v-tooltip:top="'Número de fila que se utiliza para comprobar casos de errores (no debe estar en el archivo).'">#</span></th>
								<th class="fw-bold" scope="col"><span v-tooltip:top="'Nombre del cliente.'">Nombre</span></th>
								<th class="fw-bold" scope="col"><span v-tooltip:top="'Email del cliente.'">Email</span></th>
								<th class="fw-bold" scope="col"><span v-tooltip:top="'Teléfono del cliente, con <span class=\'d-inline-block\'>signo +</span>, código de país y código de área.<br />Ej: +54116302488.'">Teléfono</span></th>
								<th class="fw-bold" scope="col"><span v-tooltip:top="'Fecha de la última compra del cliente, respetar el formato de fecha.'">Última compra</span></th>
								<th class="fw-bold text-end pe-2" scope="col"><span v-tooltip:top="'Monto total de todas las compras del cliente.'">Compra total</span></th>
							</tr>
						</template>
						<template #body>
							<tr class="table-warning" v-if="!file">
								<td></td>
								<td></td>
								<td>Columna A</td>
								<td>Columna B</td>
								<td>Columna C</td>
								<td>Columna D</td>
								<td class="text-end pe-2">Columna E</td>
							</tr>
							<tr v-for="(data, index) in form.results" v-bind:key="index">
								<td>
									<a href="javascript:void(0)" class="link-dark ms-1" @click="remove(index)"><i class="bi bi-trash text-sm"></i></a>
								</td>
								<td class="px-1 fw-bold">{{ index + 1 }}</td>
								<td :class="(data.errors.includes('name')) ? 'table-danger' : ''">{{ data.name }}</td>
								<td :class="(data.errors.includes('email')) ? 'table-danger' : ''">{{ data.email }}</td>
								<td :class="(data.errors.includes('phone')) ? 'table-danger' : ''">{{ data.phone }}</td>
								<td :class="(data.errors.includes('last_buy')) ? 'table-danger' : ''">{{ data.last_buy }}</td>
								<td :class="(data.errors.includes('total_buy')) ? 'table-danger' : ''" class="text-end pe-2">{{ (data.total_buy) ? vueNumberFormat(data.total_buy, { precision: 2 }) : '' }}</td>
							</tr>
						</template>
					</LayoutTableBasic>
				</template>
				<template #extras>
					<button type="button" class="btn btn-danger rounded-pill" @click="removeErrors" v-if="hasErrors"><i class="bi bi-trash me-1"></i>Errores</button>
				</template>
			</CommonFormWrapper>
		</main>

		<CompanyAdminCustomerModalImported ref="modalImported" />
	</div>
</template>

<script>
	import { ref, reactive, computed, watch, onMounted } from 'vue';
	import _ from 'lodash';
	import * as XLSX from 'xlsx';
	import { useToast } from 'vue-toastification';
	import composableForm from '@/composables/form';
	import CompanyAdminGroupRepository from '@/repositories/modules/Company/Admin/Group';
	import CompanyAdminCustomerRepository from '@/repositories/modules/Company/Admin/Customer';

	export default {
		props: {
			id: String
		},
		setup(props) {
			const { submitting, catchError } = composableForm();
			const toast = useToast();
			const groups = ref([]);
			const groupsOptions = computed(() => _.mapValues(_.keyBy(groups.value, 'id'), 'name'));
			const hasErrors = computed(() => { return _.some(form.results, r => r.errors.length > 0 )});
			const file = ref(null);
			const form = reactive({
				group_id: null,
				results: []
			});

			const buildRowErrors = (errorsRaw) => {
				const rowErrors = {}; // { [rowIndex]: { [field]: string[] } }

				for(const [path, messages] of Object.entries(errorsRaw)) {
					// path: "results.3.email"
					const m = path.match(/^results\.(\d+)\.(.+)$/);
					if (!m) continue;

					const rowIndex = Number(m[1]);
					const field = m[2];

					if(!(field in form.results[rowIndex].errors)) {
						form.results[rowIndex].errors.push(field);
					}
				}

				return rowErrors;
			}

			const submit = async () => {
				if(submitting.value) return;
				submitting.value = true;

				await CompanyAdminCustomerRepository.import(form).then((response) => {
					empty();
					show(response.data);
				}).catch((e) => {
					buildRowErrors(e.response.data.errors);
					catchError(e);
				});

				submitting.value = false;
			}

			const empty = () => {
				file.value = null;
				form.group_id = null;
				form.results = [];
			}

			const remove = (index) => {
				form.results.splice(index, 1);
			}

			const removeErrors = (index) => {
				_.remove(form.results, r => r.errors.length > 0);
			}

			const modalImported = ref(null);
			const show = (results) => modalImported.value.show(results);

			const dateConvert = (serial) => {
				if(typeof serial !== 'number' || isNaN(serial) || serial < 0) return null;

				try {
					const fecha = new Date(1899, 11, 30);
					fecha.setDate(fecha.getDate() + serial);

					const año = fecha.getFullYear();
					const mes = String(fecha.getMonth() + 1).padStart(2, '0');
					const dia = String(fecha.getDate()).padStart(2, '0');

					return `${año}-${mes}-${dia}`;
				}catch {
					return null;
				}
			}

			watch(() => file.value, (data) => {
				if(!data) return;
				if(!['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'].includes(data.type)) {
					empty();
					toast.error(`Formato de archivo no permitido. Debe ser .xls o .xlsx`);
					return;
				}

				const reader = new FileReader();
				reader.onload = (e) => {
					const data = new Uint8Array(e.target.result);
					const workbook = XLSX.read(data, { type: 'array' });
					const sheetName = workbook.SheetNames[0];
					const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });

					form.results = [];
					sheet.forEach((row, index) => {
						form.results.push({
							name: row[0] ?? null,
							email: row[1] ?? null,
							phone: row[2] ?? null,
							last_buy: dateConvert(row[3] ?? null),
							total_buy: Number(row[4]) ?? null,
							errors: []
						})
					});
				};
				reader.readAsArrayBuffer(data);
			});

			onMounted(() => {
				CompanyAdminGroupRepository.list().then((response) => {
					groups.value = response.data;
				}).catch((e) => catchError(e, true));
			});

			return { groupsOptions, hasErrors, file, form, submit, remove, removeErrors, modalImported, submitting };
		}
	}
</script>